import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, type Sector } from "@chicare/shared";

import { useRegisterPharmacy } from "@/lib/onboardingQueries";
import { useSystemConfig } from "@/lib/queries";
import { useStaffLogin } from "@/lib/session";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/Button";
import { BackLink } from "@/components/ui/BackLink";
import { Card } from "@/components/ui/Card";
import { LocationFields } from "@/components/ui/LocationFields";
import { Logo } from "@/app/BuyerLayout";

/**
 * Registering a pharmacy.
 *
 * ── WHY THIS ASKS FOR SO LITTLE ─────────────────────────────────────────────
 *
 * Only what is needed to create the account and start a draft: who you are, how
 * to sign in, and what the pharmacy is called. Licence numbers, bank details
 * and four document scans all come later, on a screen where the pharmacist can
 * see a checklist shrinking.
 *
 * A registration form that demands a PCN certificate before it will accept an
 * email address is one people abandon halfway through and never come back to —
 * usually because the scan is on a different device.
 *
 * ── WHY IT SIGNS THEM IN AT THE END ─────────────────────────────────────────
 *
 * The console is where onboarding is finished. Registering and then being left
 * on a page saying "now go and find the login" is a cliff, so the password they
 * just chose is used immediately and they land on their own checklist.
 *
 * ── THE FIRST QUESTION IS WHAT YOU SELL ─────────────────────────────────────
 *
 * Before the name of the business, before the address. It decides which licence
 * we ask for, which catalogue they list from, and which shelf their stock lands
 * on — and getting it wrong sends a pharmacist a checklist demanding a
 * veterinary surgeon, or an agrovet store one demanding a pharmacist.
 *
 * Two cards rather than a dropdown, because a dropdown defaulted to the first
 * option is a question most people never notice they answered.
 *
 * The one exception is arriving from the animal-health side of the shop, or
 * from a link that says "Sell veterinary on ChiCare" in so many words. Somebody
 * who has just pressed that has answered this question already, so `?sector=`
 * ticks the box for them — visibly, on a card they can untick — rather than
 * making them say it twice.
 *
 * ── WHY THE PITCH IS NOT WRITTEN HERE ───────────────────────────────────────
 *
 * The heading, the introduction, the reasons to join and the note about what
 * comes next are all admin-owned settings. This is the page that decides
 * whether a pharmacy signs up at all, so the people talking to pharmacies get
 * to rewrite it without waiting for a build. The form below is code; every word
 * above and around it is not.
 */
export function PharmacyRegisterScreen() {
  const navigate = useNavigate();
  const register = useRegisterPharmacy();
  const login = useStaffLogin();
  const { data: config } = useSystemConfig();

  const pitch = config?.seller_pitch;

  const [form, setForm] = useState({
    name: "",
    premises_address: "",
    lga: "",
    state: "",
    contact_name: "",
    contact_phone: "",
    email: "",
    password: "",
  });

  // Nothing preselected, unless the link they followed already said it: an
  // unanswered question stops the form, but a wrongly assumed answer sends
  // somebody a checklist they cannot complete. Only 'veterinary' is honoured,
  // and only as a starting tick — anything else, including a mistyped or
  // tampered value, leaves the question open exactly as before.
  const [searchParams] = useSearchParams();
  const [sectors, setSectors] = useState<Sector[]>(
    searchParams.get("sector") === "veterinary" ? ["veterinary"] : [],
  );

  /**
   * Selling animal health, and NOTHING else.
   *
   * The line between "a pharmacy" and "not a pharmacy". A vet practice or an
   * agrovet store has no PCN licence and no superintendent pharmacist, and will
   * never have either — so every word on this form that assumes one is a word
   * telling them they have come to the wrong place.
   *
   * A pharmacy that also stocks dog dewormer is still a pharmacy, and reads the
   * pharmacy wording. Both boxes ticked is not this case.
   */
  const vetOnly = sectors.length === 1 && sectors[0] === "veterinary";

  const toggleSector = (sector: Sector) =>
    setSectors((current) =>
      current.includes(sector)
        ? current.filter((one) => one !== sector)
        : [...current, sector],
    );

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  // ── THE PITCH, IN WHICHEVER VOICE FITS ──────────────────────────────────
  //
  // Derived here rather than beside `pitch` above, because all three read
  // `vetOnly`, and `vetOnly` reads the sector state declared above it.
  //
  // Each falls back to the pharmacy wording when the veterinary version has
  // been emptied by an admin, so clearing one of those fields removes a
  // DIFFERENCE rather than removing the text — a veterinary seller never ends
  // up reading a blank page because somebody tidied a setting.
  const intro = (
    (vetOnly ? pitch?.vet_intro : pitch?.intro) || pitch?.intro
  )?.trim();

  const documentsNote = (
    (vetOnly ? pitch?.vet_documents_note : pitch?.documents_note) ||
    pitch?.documents_note
  )?.trim();

  // One reason per line, blanks dropped — so an admin can add or remove a
  // bullet by pressing Enter, which is the only editing model that survives
  // contact with somebody who is not a developer.
  const benefits = (
    (vetOnly ? pitch?.vet_benefits : pitch?.benefits) ||
    pitch?.benefits ||
    ""
  )
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const errors =
    register.error instanceof ApiError ? register.error.fieldErrors : {};
  const generalError =
    register.error instanceof ApiError && !register.error.isValidation
      ? register.error.message
      : null;

  const busy = register.isPending || login.isPending;

  const complete =
    sectors.length > 0 &&
    form.name.trim().length >= 3 &&
    form.premises_address.trim().length > 0 &&
    form.contact_name.trim().length > 0 &&
    form.contact_phone.trim().length > 0 &&
    form.email.trim().length > 0 &&
    form.password.length >= 10;

  const submit = () => {
    register.mutate(
      { ...form, sectors },
      {
        onSuccess: () => {
          // Straight into the console rather than a dead end that says
          // "now sign in somewhere else".
          login.mutate(
            { email: form.email, password: form.password },
            {
              onSuccess: () =>
                navigate("/seller/onboarding", { replace: true }),
            },
          );
        },
      },
    );
  };

  return (
    <div className="min-h-dvh bg-paper">
      <div className="mx-auto w-full max-w-[620px] px-4 py-8">
        {/* This page is reached from the buyer account screen as often as from
            a marketing link, and a pharmacist who reads it and decides to think
            about it must not be stranded here. */}
        <BackLink className="mb-2" fallbackLabel="Go to ChiCare" />

        <Link
          to="/"
          className="mb-5 inline-flex min-h-11 items-center gap-1.5 font-display text-[18px] font-extrabold tracking-[-0.02em] text-ink no-underline"
        >
          <span className="grid h-[24px] w-[24px] place-items-center rounded-[7px] bg-blue">
            <Logo className="h-[14px] w-[14px] fill-white" />
          </span>
          ChiCare
        </Link>

        {/* ── THE PITCH, ON THE BRAND ──────────────────────────────
            A 28px heading, a paragraph of grey prose and a column of grey
            bullets, all on a grey page. Nothing on it said ChiCare, and the
            argument for selling here was the least visible thing on the
            screen.

            Same gradient as the trust banner and the prescription panel. The
            benefits move inside it, where white-on-blue makes them the first
            thing read rather than the last. */}
        <section
          data-testid="seller-pitch"
          className="relative overflow-hidden rounded-[16px] bg-[linear-gradient(135deg,var(--color-blue-deep),var(--color-blue))] px-5 py-6 text-white"
        >
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_88%_-10%,rgba(255,255,255,0.18),transparent_58%)]"
          />

          <div className="relative">
            <h1 className="m-0 font-display text-[25px] font-bold leading-[1.15] tracking-[-0.025em]">
              {pitch?.heading?.trim() || "Sell on ChiCare"}
            </h1>

            {intro ? (
              <p
                data-testid="seller-pitch-intro"
                className="m-0 mt-1.5 max-w-[46ch] whitespace-pre-line text-[13px] leading-snug text-white/75"
              >
                {intro}
              </p>
            ) : null}

            {benefits.length > 0 ? (
              <ul
                data-testid="seller-pitch-benefits"
                className="m-0 mt-4 list-none space-y-[7px] p-0"
              >
                {benefits.map((benefit) => (
                  <li
                    key={benefit}
                    className="flex items-start gap-2.5 text-[13px] leading-snug text-white"
                  >
                    {/* A dot, not a tick. These are selling points, not things
                        that have been completed, and a column of ticks read as
                        a checklist somebody had already passed. */}
                    <span
                      aria-hidden
                      className="mt-[6px] h-[5px] w-[5px] shrink-0 rounded-full bg-white/70"
                    />
                    <span className="min-w-0">{benefit}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </section>

        <Card className="mt-4">
          {/* The hint used to be three clauses explaining that the choice
              decides the licence and the catalogue and could be changed later.
              The cards below name their own licence, and nothing here is sent
              until the button at the bottom, so all three were answering
              questions the screen already answers. */}
          <Section step={1} title="What do you sell?" hint="Pick both if you sell both.">
            <div className="grid gap-2.5 sm:grid-cols-2">
              <SectorChoice
                selected={sectors.includes("human")}
                onToggle={() => toggleSector("human")}
                title="Human medicines"
                blurb="Retail or community pharmacy."
                needs="PCN licence + superintendent pharmacist"
              />
              <SectorChoice
                selected={sectors.includes("veterinary")}
                onToggle={() => toggleSector("veterinary")}
                title="Animal health"
                blurb="Clinic, agrovet store or farm supplier."
                needs="VCN registration + vet in charge"
              />
            </div>

            {/* Shown when somebody sells BOTH, and only then.

                It exists to explain that the animal-health half is checked
                against a different regulator — which is news to a pharmacy
                that has just ticked a second box, and is not news to an
                agrovet store, whose whole page already says so. Shown to the
                latter it was the same sentence twice in half a screen.

                Admin-owned like every other word here, because the people
                talking to agrovet stores are the ones who know how. */}
            {sectors.includes("veterinary") &&
            sectors.includes("human") &&
            pitch?.veterinary_intro?.trim() ? (
              <p className="m-0 border-t border-line pt-3 text-[12.5px] leading-relaxed text-muted">
                {pitch.veterinary_intro}
              </p>
            ) : null}

            {errors.sectors?.[0] ? (
              <p
                role="alert"
                className="m-0 text-[12px] font-medium text-danger"
              >
                {errors.sectors[0]}
              </p>
            ) : null}
          </Section>

          <Section step={2} title="Your business">
            <Field
              label={vetOnly ? "Business name" : "Pharmacy name"}
              value={form.name}
              onChange={set("name")}
              error={errors.name?.[0]}
              placeholder={
                sectors.length === 1 && sectors[0] === "veterinary"
                  ? "Kubwa Agrovet Stores"
                  : "Unity Pharmacy Kubwa"
              }
            />
            <Field
              label="Premises address"
              value={form.premises_address}
              onChange={set("premises_address")}
              error={errors.premises_address?.[0]}
              placeholder="12 Gado Nasko Road"
            />
            {/* Picked, never typed. A pharmacy registered in "Wuse" never
                matches a delivery zone priced for "Abuja Municipal", and the
                failure is silent — every order to them quietly pays the
                platform default. */}
            <LocationFields
              state={form.state}
              lga={form.lga}
              onState={set("state")}
              onLga={set("lga")}
              stateError={errors.state?.[0]}
              lgaError={errors.lga?.[0]}
            />
          </Section>

          <Section step={3} title="Who we speak to">
            <Field
              label="Your name"
              value={form.contact_name}
              onChange={set("contact_name")}
              error={errors.contact_name?.[0]}
              placeholder="Ngozi B."
            />
            <Field
              label="Phone"
              value={form.contact_phone}
              onChange={set("contact_phone")}
              error={errors.contact_phone?.[0]}
              inputMode="tel"
              placeholder="0803 123 4567"
            />
          </Section>

          <Section
            step={4}
            title="How you sign in"
            hint="Email and password — separate from the buyer app."
          >
            <Field
              label="Work email"
              value={form.email}
              onChange={set("email")}
              error={errors.email?.[0]}
              type="email"
              placeholder={
                vetOnly ? "you@yourclinic.ng" : "you@yourpharmacy.ng"
              }
            />
            <Field
              label="Password"
              value={form.password}
              onChange={set("password")}
              error={errors.password?.[0]}
              type="password"
              hint="At least 10 characters. This login moves money."
            />
          </Section>

          {generalError ? (
            <p
              role="alert"
              className="mb-3 text-[13px] font-medium text-danger"
            >
              {generalError}
            </p>
          ) : null}

          <Button
            size="lg"
            className="w-full"
            disabled={!complete || busy}
            onClick={submit}
          >
            {/* The bug this fixes: an agrovet store filled in the whole
                form, ticked Animal health, and the last thing it read before
                submitting was "Create my pharmacy account". */}
            {busy
              ? "Creating your account…"
              : (vetOnly
                  ? pitch?.vet_submit_cta?.trim()
                  : pitch?.submit_cta?.trim()) ||
                (vetOnly
                  ? "Create my seller account"
                  : "Create my pharmacy account")}
          </Button>

          {/* And the same again underneath, which told them to go and find a
              PCN licence and a superintendent pharmacist's certificate. */}
          {documentsNote ? (
            <p
              data-testid="documents-note"
              className="m-0 mt-3 whitespace-pre-line text-[12px] leading-relaxed text-muted"
            >
              {documentsNote}
            </p>
          ) : null}
        </Card>

        {/* min-h-11 on the link itself, not the paragraph: §11 measures the
            TARGET, and an inline link in a 13px sentence is about 18px tall —
            which is the shape a thumb misses. */}
        <p className="mt-5 text-center text-[13px] text-muted">
          Already registered?{" "}
          <Link
            to="/seller/login"
            className="inline-flex min-h-11 items-center font-medium text-blue"
          >
            Sign in to your console
          </Link>
        </p>
      </div>
    </div>
  );
}

/**
 * One of the two shops, as a card you press.
 *
 * A checkbox and a label would be a third of the height and a tenth as clear.
 * Naming the licence each side needs on the card itself is what stops an
 * agrovet store getting three screens further before discovering we want a
 * pharmacist's registration number.
 *
 * The veterinary card carries the violet border of the veterinary end, which is
 * the same colour they will see for the rest of their time in that half of the
 * product — so the association starts here rather than being learnt later.
 */
function SectorChoice({
  selected,
  onToggle,
  title,
  blurb,
  needs,
}: {
  selected: boolean;
  onToggle: () => void;
  title: string;
  blurb: string;
  needs: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      onClick={onToggle}
      className={cn(
        "rounded-card border p-3.5 text-left",
        selected
          ? "border-blue bg-blue-tint-2"
          : "border-line bg-white hover:border-faint",
      )}
    >
      <span className="flex items-start gap-2.5">
        <span
          aria-hidden
          className={cn(
            "mt-0.5 grid h-[18px] w-[18px] shrink-0 place-items-center rounded-[5px] border-2",
            selected ? "border-blue bg-blue" : "border-line bg-white",
          )}
        >
          {selected ? (
            <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden>
              <path
                d="M2.5 6.2l2.4 2.4 4.6-5"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : null}
        </span>

        <span className="min-w-0">
          <span className="block text-[14px] font-bold text-ink">{title}</span>
          <span className="mt-0.5 block text-[12.5px] leading-relaxed text-muted">
            {blurb}
          </span>
          <span className="mt-2 block font-mono text-[9.5px] uppercase leading-snug tracking-[0.08em] text-faint">
            {needs}
          </span>
        </span>
      </span>
    </button>
  );
}

/**
 * One step of the form.
 *
 * The number is the point. Four unnumbered headings separated by grey rules
 * read as an unbounded list — somebody halfway down has no idea whether two
 * more are coming or ten. Numbering them says how much is left, which is the
 * single cheapest thing you can do for a form's completion rate.
 *
 * Mono numerals, like every other number in this app.
 */
function Section({
  step,
  title,
  hint,
  children,
}: {
  step: number;
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-5 border-b border-line pb-5 last:mb-0 last:border-b-0 last:pb-0">
      <div className="mb-1 flex items-center gap-2">
        <span
          aria-hidden
          className="grid h-[19px] w-[19px] shrink-0 place-items-center rounded-full bg-blue-tint font-mono text-[10px] font-bold leading-none text-blue"
        >
          {step}
        </span>
        <h2 className="m-0 font-display text-[15px] font-bold tracking-[-0.01em]">
          {title}
        </h2>
      </div>
      {hint ? (
        <p className="m-0 mb-3 ml-[27px] text-[12px] leading-snug text-muted">{hint}</p>
      ) : null}
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  error,
  hint,
  type = "text",
  inputMode,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  type?: string;
  inputMode?: "tel" | "text" | "email";
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[12.5px] font-medium text-ink">
        {label}
      </span>
      <input
        type={type}
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          "min-h-12 w-full rounded-btn border bg-white px-3 text-[14px] text-ink outline-none focus:border-blue",
          error ? "border-danger" : "border-line",
        )}
      />
      {error ? (
        <span
          role="alert"
          className="mt-1 block text-[12px] font-medium text-danger"
        >
          {error}
        </span>
      ) : hint ? (
        <span className="mt-1 block text-[11.5px] leading-relaxed text-muted">
          {hint}
        </span>
      ) : null}
    </label>
  );
}
